import React, { ReactElement } from "react";

import { WizardStepProps } from "./WizardStep";
import Panel from "../panels/Panel";
import { Side, Size } from "../../types/system/Layout";
import Spacer from "../containers/Spacer";

import "./Wizard.scss";
import Utilities from "../../foundation/Utility";

export enum WizardSlidePosition {
    /** Step is positioned to the left (already passed) */
    Left = "left",
    /** Step is positioned to the right (upcoming) */
    Right = "right",
    /** Step is currently visible in the center */
    Middle = "middle",
}

interface WizardProps {
    step?: number;
    width?: number;
    height?: string;
    controls?: boolean;
    frameless?: boolean;
    transparent?: boolean;
    flow?: boolean;

    clearanceSide?: Side;
    clearanceSize?: Size;

    children?: ReactElement<WizardStepProps> | ReactElement<WizardStepProps>[];

    stepChanged?: (step: number) => void;
    shouldComplete?: () => void;
    shouldAbort?: () => void;
}

interface WizardState {
    step: number;
    oldStep: number;
}

export const WizardContext = React.createContext({
    step: 0,
    oldStep: 0,

    canPreviousText: "",
    canProceedText: "",
    canCompleteText: "",
    canAbortText: "",

    canPrev: (): boolean => false,
    canNext: (): boolean => false,
    canComplete: (): boolean => false,
    canAbort: (): boolean => false,
});

class Wizard extends React.Component<WizardProps, WizardState> {
    state = {
        step: 0,
        oldStep: 0,
    } as WizardState;

    componentDidMount(): void {
        if (this.props.step)
            this.setState({ step: this.props.step, oldStep: this.props.step });
    }

    componentDidUpdate(): void {
        if (this.props.step !== this.state.step)
            this.setState({ step: this.props.step, oldStep: this.state.step });
    }

    setStep(step: number) {
        if (step !== this.state.step) {
            this.setState({ step, oldStep: this.state.step });
            if (this.props.stepChanged) this.props.stepChanged(step);
        }
    }

    render() {
        const contextValue = {
            step: this.state.step,
            oldStep: this.state.oldStep,

            canPreviousText: undefined,
            canProceedText: undefined,
            canCompleteText: undefined,
            canAbortText: undefined,

            internalCanPrev: () => true,
            internalCanNext: () => true,
            internalCanComplete: () => true,
            internalCanAbort: () => true,

            canPrev: () => this.state.step > 0,
            canNext: () =>
                React.Children.toArray(this.props.children).find(
                    (_, index) => index === this.state.step + 1,
                ) !== undefined,
            canComplete: () =>
                React.Children.toArray(this.props.children).find(
                    (_, index) => index === this.state.step + 1,
                ) === undefined,
            canAbort: () => true,

            setStepValues: (
                canPreviousText?: string,
                canProceedText?: string,
                canCompleteText?: string,
                canAbortText?: string,
                canPrevious?: () => boolean,
                canNext?: () => boolean,
                canComplete?: () => boolean,
                canAbort?: () => boolean,
            ) => {
                contextValue.canPreviousText = canPreviousText;
                contextValue.canProceedText = canProceedText;
                contextValue.canCompleteText = canCompleteText;
                contextValue.canAbortText = canAbortText;
                contextValue.internalCanPrev = canPrevious ?? (() => true);
                contextValue.internalCanNext = canNext ?? (() => true);
                contextValue.internalCanComplete = canComplete ?? (() => true);
                contextValue.internalCanAbort = canAbort ?? (() => true);
            },
        };

        let classes = "wizard";

        // Clearance classes
        if (!!this.props.clearanceSide) {
            classes += ` ${Utilities.getSideClass(
                this.props.clearanceSide,
            )}clearance${Utilities.getSizeClass(this.props.clearanceSize)}`;
        }

        if (this.props.frameless) classes += " frameless";
        if (this.props.transparent) classes += " transparent";
        if (this.props.flow) classes += " flow";

        return (
            <WizardContext.Provider value={contextValue}>
                <Panel
                    className={classes}
                    bufferSide={Side.All}>
                    <Panel
                        bufferSide={Side.All}
                        style={{
                            width: this.props.width,
                            height: this.props.height,
                        }}>
                        <div
                            className="wizard-steps"
                            style={{
                                left: `calc(-${this.state.step * 100}% - ${this.state.step * 20}px)`,
                            }}>
                            {this.props.children}
                        </div>
                    </Panel>
                    {this.props.controls && (
                        <footer className="top-clearance full">
                            {contextValue.canPrev() && (
                                <button
                                    disabled={!contextValue.internalCanPrev()}
                                    className="previous"
                                    data-tip={contextValue.canPreviousText}
                                    onClick={() =>
                                        this.setStep(this.state.step - 1)
                                    }>
                                    <i>arrow_back</i>
                                </button>
                            )}
                            <Spacer />
                            {contextValue.canAbort() && (
                                <button
                                    disabled={!contextValue.internalCanAbort()}
                                    className="abort"
                                    data-tip={contextValue.canAbortText}
                                    onClick={this.props.shouldAbort}>
                                    <i>snooze</i>
                                </button>
                            )}
                            {contextValue.canComplete() && (
                                <button
                                    disabled={
                                        !contextValue.internalCanComplete()
                                    }
                                    className="complete"
                                    data-tip={contextValue.canCompleteText}
                                    onClick={this.props.shouldComplete}>
                                    <i>check</i>
                                </button>
                            )}
                            {contextValue.canNext() && (
                                <button
                                    disabled={!contextValue.internalCanNext()}
                                    className="next"
                                    data-tip={contextValue.canProceedText}
                                    onClick={() =>
                                        this.setStep(this.state.step + 1)
                                    }>
                                    <i>arrow_forward</i>
                                </button>
                            )}
                        </footer>
                    )}
                </Panel>
            </WizardContext.Provider>
        );
    }
}

export default Wizard;
